import React, { useState, useEffect, useContext } from 'react';
import apiClient from '../api/client';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Alert, AlertDescription } from './ui/alert';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from './ui/carousel';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Upload, Check, Trash2 } from 'lucide-react';



function LetterheadManagement() {
    const { user } = useAuth();
    const [letterheads, setLetterheads] = useState([]);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [newLetterhead, setNewLetterhead] = useState({
        name: '',
        file: null,
        is_default: false
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchLetterheads();
    }, []);

    const fetchLetterheads = async () => {
        try {
            const response = await apiClient.get(`/letterheads`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            setLetterheads(response.data);
        } catch (error) {
            console.error('Error fetching letterheads:', error);
        }
    };

    const handleFileChange = (e) => {
        setNewLetterhead({ ...newLetterhead, file: e.target.files[0] });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewLetterhead({ ...newLetterhead, [name]: value });
    };

    const handleCheckboxChange = (checked) => {
        setNewLetterhead({ ...newLetterhead, is_default: checked });
    };

    const uploadLetterhead = async () => {
        if (!newLetterhead.file || !newLetterhead.name) {
            alert('Please provide a name and select a file');
            return;
        }

        setLoading(true);
        
        try {
            const formData = new FormData();
            formData.append('name', newLetterhead.name);
            formData.append('file', newLetterhead.file);
            formData.append('is_default', newLetterhead.is_default);

            await apiClient.post(`/letterheads`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${localStorage.getItem('token')}`
                }
            });
            
            fetchLetterheads();
            setShowUploadModal(false);
            setNewLetterhead({ name: '', file: null, is_default: false });
        } catch (error) {
            console.error('Error uploading letterhead:', error);
        } finally {
            setLoading(false);
        }
    };

    const setDefaultLetterhead = async (letterheadId) => {
        try {
            await apiClient.put(
                `/letterheads/${letterheadId}/set-default`,
                {},
                { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
            );
            fetchLetterheads();
        } catch (error) {
            console.error('Error setting default letterhead:', error);
        }
    };

    const deleteLetterhead = async (letterheadId) => {
        if (!window.confirm('Are you sure you want to delete this letterhead?')) return;
        
        try {
            await apiClient.delete(`/letterheads/${letterheadId}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            fetchLetterheads();
        } catch (error) {
            console.error('Error deleting letterhead:', error);
        }
    };

    return (
        <Card>
            <CardHeader>
                <div className="flex justify-between items-center">
                    <CardTitle>Letterhead Management</CardTitle>
                    <Button onClick={() => setShowUploadModal(true)}>
                        <Upload className="mr-2" size={16} /> Upload Letterhead
                    </Button>
                </div>
            </CardHeader>
            <CardContent>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Uploaded By</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead>Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {letterheads.map(letterhead => (
							<div key={letterhead.letterhead_id}> {/* Add this key */}
                                <TableCell className="font-medium">{letterhead.name}</TableCell>
                                <TableCell>
                                    {letterhead.is_default ? (
                                        <Badge className="bg-green-100 text-green-800">Default</Badge>
                                    ) : (
                                        <Badge>Active</Badge>
                                    )}
                                </TableCell>
                                <TableCell>{letterhead.created_by}</TableCell>
                                <TableCell>{new Date(letterhead.created_at).toLocaleDateString()}</TableCell>
                                <TableCell>
                                    <div className="flex space-x-2">
                                        {!letterhead.is_default && (
                                            <Button 
                                                size="sm"
                                                onClick={() => setDefaultLetterhead(letterhead.letterhead_id)}
                                            >
                                                <Check className="mr-1" size={14} /> Set Default
                                            </Button>
                                        )}
                                        {!letterhead.is_default && (
                                            <Button 
                                                size="sm" 
                                                variant="destructive"
                                                onClick={() => deleteLetterhead(letterhead.letterhead_id)}
                                            >
                                                <Trash2 className="mr-1" size={14} /> Delete
                                            </Button>
                                        )}
                                    </div>
                                </TableCell>
                            </div>
                        ))}
                    </TableBody>
                </Table>
            </CardContent>

            {/* Upload Modal */}
            <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Upload New Letterhead</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="letterheadName">Letterhead Name</Label>
                            <Input
                                id="letterheadName"
                                name="name"
                                value={newLetterhead.name}
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                        <div>
                            <Label htmlFor="letterheadFile">Word Document</Label>
                            <Input
                                id="letterheadFile"
                                type="file"
                                accept=".pdf"
                                onChange={handleFileChange}
                                required
                            />
                        </div>
                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="defaultLetterhead"
                                checked={newLetterhead.is_default}
                                onCheckedChange={handleCheckboxChange}
                            />
                            <Label htmlFor="defaultLetterhead">Set as default letterhead</Label>
                        </div>
                        <div className="flex justify-end space-x-4">
                            <Button 
                                onClick={uploadLetterhead}
                                disabled={loading}
                            >
                                {loading ? 'Uploading...' : 'Upload Letterhead'}
                            </Button>
                            <Button 
                                variant="outline" 
                                onClick={() => {
                                    setShowUploadModal(false);
                                    setNewLetterhead({ name: '', file: null, is_default: false });
                                }}
                            >
                                Cancel
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </Card>
    );
}

export default LetterheadManagement;